import { PolicyDefinition } from "./policyTypes";

export const polUpdated: PolicyDefinition[] = [
  {
    id: "upd-cctv",
    code: "GOV-007",
    title: "CCTV and Surveillance",
    category: "governance",
    cqcFocus: "Safe",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "Controls the lawful, proportionate and transparent use of CCTV to protect residents, staff and visitors while upholding privacy and data rights under UK GDPR and the Data Protection Act 2018.",
    keyPoints: [
      "Cameras never cover bedrooms, bathrooms or toilets.",
      "Clear signage is displayed at every entrance and monitored area.",
      "Footage is retained for a maximum of 30 days unless an investigation requires longer.",
      "All access to footage is authorised, logged and audited.",
    ],
    relatedRegulations: [
      "UK GDPR and Data Protection Act 2018",
      "ICO surveillance camera code of practice",
      "Regulation 12 Safe Care and Treatment",
      "Regulation 17 Good Governance",
      "Human Rights Act 1998 Article 8",
    ],
    sections: [
      { title: "1. Introduction and purpose", lines: [
        "Roop Cottage Residential Care Home uses CCTV to protect the safety and security of residents, staff and visitors, to deter crime and to support the investigation of incidents.",
        "This policy is written under the CQC single assessment framework and the fundamental standards, and is read alongside the Data Protection policy.",
      ]},
      { title: "2. Scope and camera locations", lines: [
        "Cameras cover entrance and exit points, corridors, lounges, dining areas, offices and storerooms.",
        "Cameras are never installed in bedrooms, bathrooms, toilets or kitchen food preparation areas.",
        "Any new camera requires a documented necessity and proportionality assessment and a data protection impact assessment.",
      ]},
      { title: "3. Data protection and retention", lines: [
        "Footage is processed lawfully under UK GDPR and the Data Protection Act 2018 for the purposes stated in this policy only.",
        "Footage is automatically overwritten within 30 days unless a specific investigation requires retention, which is recorded with reasons.",
        "Recordings are stored securely with encryption and restricted physical access.",
      ]},
      { title: "4. Access, sharing and subject access requests", lines: [
        "Only authorised staff with a legitimate need may view footage; every viewing is logged with date, time, reason and person.",
        "Footage is shared with police or authorities only when necessary and recorded in the data sharing register.",
        "Subject access requests are handled within one calendar month using Appendix B.",
      ]},
      { title: "5. Risks and mitigations", lines: [
        "Privacy intrusion is mitigated by signage, camera placement reviews and resident and family information.",
        "Misuse is mitigated by role based access, logging and audit.",
        "Breach risk is mitigated by encryption, secure storage and staff training.",
      ]},
      { title: "6. Monitoring, training and review", lines: [
        "The system is checked monthly for image quality, time stamp accuracy and retention settings.",
        "Authorised viewers complete CCTV and data protection training before access is granted.",
        "This policy is reviewed annually and after any significant event or regulatory change.",
      ]},
      { title: "7. Local authority and regulator requirements", lines: [
        "Wakefield Council adult social care requirements and CQC fundamental standards are complied with.",
        "Current contact details for the council and CQC are held in the compliance contacts appendix of this library.",
      ]},
      { title: "Appendix A - Signage and transparency", lines: [
        "Signage at every entrance and monitored area states that CCTV is in operation, the purpose and who to contact.",
        "Residents and families are informed on admission and at review; leaflets are available in easy read and large print.",
      ]},
      { title: "Appendix B - Access request procedure", lines: [
        "1. Request received in writing or verbally and logged the same day.",
        "2. Identity verified and scope of request clarified.",
        "3. Manager and data protection lead review within 7 days.",
        "4. Third party images redacted where required.",
        "5. Response issued within one calendar month with a record kept.",
      ]},
    ],
  },
  {
    id: "upd-safeguarding",
    code: "SAF-006",
    title: "Safeguarding Adults",
    category: "safeguarding",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Sets out how Roop Cottage prevents, identifies, reports and responds to abuse and neglect using the six Care Act principles and Making Safeguarding Personal, with clear timelines and a no blame reporting culture.",
    keyPoints: [
      "Any concern is reported to the designated safeguarding lead the same day.",
      "The local authority is notified the same working day where section 42 criteria appear met.",
      "The resident's wishes are central through Making Safeguarding Personal.",
      "All staff complete level 2 safeguarding training annually plus Oliver McGowan training.",
    ],
    relatedRegulations: [
      "Care Act 2014 sections 42 to 43",
      "Regulation 13 Safeguarding Service Users from Abuse and Improper Treatment",
      "Regulation 20A Duty of Candour",
      "Safeguarding Vulnerable Groups Act 2006",
    ],
    sections: [
      { title: "1. Introduction and purpose", lines: [
        "Roop Cottage Residential Care Home is committed to a safe and caring environment; safeguarding is everyone's responsibility.",
        "This policy applies the CQC single assessment framework safe theme and Making Safeguarding Personal.",
      ]},
      { title: "2. Scope and definitions", lines: [
        "Applies to all staff, volunteers, contractors and agency workers.",
        "An adult at risk is an adult with care and support needs who is unable to protect themselves from harm or exploitation.",
      ]},
      { title: "3. The six principles", lines: [
        "Empowerment, prevention, proportionality, protection, partnership and accountability guide every decision.",
      ]},
      { title: "4. Types of abuse", lines: [
        "Physical, emotional, sexual, financial, neglect, discriminatory, organisational, self neglect, modern slavery and domestic abuse.",
      ]},
      { title: "5. Procedure: identify, report, respond", lines: [
        "1. Secure immediate safety and give first aid if needed.",
        "2. Report to the designated safeguarding lead or manager the same day; anonymous reporting is available.",
        "3. The manager assesses, records and preserves evidence.",
        "4. Notify the local authority the same working day where section 42 criteria appear met, with written follow up within 48 hours.",
        "5. Notify CQC where required and the police where a crime is suspected.",
        "6. Inform family or representatives unless doing so would increase risk.",
      ]},
      { title: "6. Investigation, action and support", lines: [
        "Cooperate fully with local authority enquiries and keep the resident at the centre.",
        "Apply interim protection measures; make LADO or DBS referrals where criteria apply.",
        "Offer the resident advocacy, emotional support and connection to support services.",
        "Hold a learning review after every enquiry and share lessons with the team.",
      ]},
      { title: "7. Prevention, training and culture", lines: [
        "Safer recruitment, supervision and safe staffing reduce risk.",
        "Level 2 safeguarding training annually, Oliver McGowan training, and case studies in training.",
        "Concerns can be raised without fear of reprisal; whistleblowing is protected.",
      ]},
      { title: "8. Local authority and regulator requirements", lines: [
        "Wakefield Council requires a named designated safeguarding officer, reporting and investigation systems and action to prevent recurrence.",
        "Contacts: Wakefield Council compliance and safeguarding team, CQC safeguarding helpline 03000 610610, police 999 in emergency.",
      ]},
      { title: "Appendix A - Types of abuse quick reference", lines: [
        "Physical: hitting, restraint. Emotional: threats, humiliation, isolation. Sexual: non consensual contact.",
        "Financial: theft, coercion over money. Neglect: omission of care. Organisational: rigid routines over needs.",
        "Discriminatory: abuse based on identity. Self neglect: hoarding, refusing care. Modern slavery and domestic abuse.",
      ]},
      { title: "Appendix B - Referral timelines and contacts", lines: [
        "Internal report: same day. Local authority: same working day, written within 48 hours.",
        "CQC: within statutory notification timescales. Police: immediately for suspected crime.",
        "All contacts are verified and updated at each annual review.",
      ]},
    ],
  },
  {
    id: "upd-recruitment",
    code: "GOV-008",
    title: "Safer Recruitment and Retention",
    category: "governance",
    cqcFocus: "Well led",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures every person employed is suitable, vetted, valued and supported to stay, using fair and transparent processes that meet Regulation 19, UKVI rules and Wakefield Council requirements.",
    keyPoints: [
      "Enhanced DBS and barred list checks are completed before the first working day.",
      "Two verified references and explored gaps in employment history are mandatory.",
      "Overseas police checks are required for skilled workers per UKVI until a DBS is in place.",
      "Every new starter has induction, a named mentor and a probation review.",
    ],
    relatedRegulations: [
      "Regulation 19 Fit and Proper Persons Employed",
      "Safeguarding Vulnerable Groups Act 2006",
      "Equality Act 2010",
      "UKVI sponsor and right to work requirements",
    ],
    sections: [
      { title: "1. Introduction and purpose", lines: [
        "Our staff are our most important asset. Recruitment is fair, transparent, impartial and compliant with CQC and Wakefield Council requirements.",
      ]},
      { title: "2. Scope", lines: [
        "Covers advertising, applications, shortlisting, interviews, pre-employment checks, offers and induction for all roles.",
      ]},
      { title: "3. Essential requirements", lines: [
        "Understanding of safeguarding adults, compassion, effective communication and teamwork.",
        "Skilled workers must provide two references, all education certificates and a police check from their country of origin valid at the point of entry to the UK, as mandated by UKVI.",
      ]},
      { title: "4. Recruitment process", lines: [
        "1. Advertise on job boards, social media and the council site.",
        "2. Receive and acknowledge applications.",
        "3. Shortlist against the essential criteria only.",
        "4. Interview with a panel of at least two people using values based scenarios.",
        "5. Complete all pre-employment checks in Appendix A before start.",
        "6. Issue the offer and contract.",
        "7. Start induction with a named mentor.",
      ]},
      { title: "5. References and background checks", lines: [
        "Two references from previous employers, verified before the start date.",
        "Enhanced DBS with barred list check, right to work, identity and qualification verification.",
        "All personal information is kept confidential under UK GDPR and the Data Protection Act 2018.",
      ]},
      { title: "6. Onboarding, induction and retention", lines: [
        "Orientation tour, role specific training and a named mentor for the first months.",
        "Probation and performance reviews identify development early.",
        "Competitive pay, recognition and career progression support retention.",
      ]},
      { title: "7. Diversity, equality and monitoring", lines: [
        "We recruit a workforce that reflects our residents and community, with reasonable adjustments at every stage.",
        "Recruitment data is monitored and the policy reviewed annually or after regulatory change.",
      ]},
      { title: "8. Local authority and regulator requirements", lines: [
        "Wakefield Council requires advertising, interviews, reference contact, induction and monitoring of recruitment.",
        "Evidence for Regulation 19 is kept complete and available for inspection.",
      ]},
      { title: "Appendix A - Pre-employment checklist", lines: [
        "Application and interview notes, identity, right to work, enhanced DBS plus update service enrolment, barred list check, two verified references, gaps explored, qualifications verified, overseas police check where applicable, health declaration and signed induction plan.",
      ]},
      { title: "Appendix B - Values based interview prompts", lines: [
        "Tell us about a time you showed compassion. What would you do if you saw a colleague speak disrespectfully to a resident? How do you protect dignity during personal care? Describe a time you raised a concern.",
      ]},
    ],
  },
  {
    id: "upd-incidents",
    code: "GOV-009",
    title: "Incidents and Accidents",
    category: "governance",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures every incident, accident and near miss is reported promptly, investigated thoroughly, supported well and learned from, with CQC notified within statutory timescales and a just culture throughout.",
    keyPoints: [
      "All incidents and near misses are reported by the end of the shift.",
      "Serious incidents are notified to CQC without delay and recorded on the register.",
      "Root cause reviews are completed within 10 working days.",
      "Learning is shared with the whole team and trends reviewed monthly.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
      "Regulation 18 Notification of Incidents",
      "Regulation 20A Duty of Candour",
      "RIDDOR 2013 where applicable",
    ],
    sections: [
      { title: "1. Introduction and purpose", lines: [
        "Roop Cottage is committed to a safe and secure environment; when incidents occur they are managed well and learned from.",
        "A just culture applies: near misses are reported without blame so the whole team can learn.",
      ]},
      { title: "2. Scope", lines: [
        "Covers all incidents involving residents, staff and visitors, including falls, injuries, illness, medication errors, near misses, safeguarding concerns, infection events, fire and missing persons.",
      ]},
      { title: "3. Reporting", lines: [
        "The witness informs the manager immediately and completes the incident form by the end of shift.",
        "The form records date and time, people involved, description, injuries and action taken.",
      ]},
      { title: "4. Grading and immediate response", lines: [
        "The manager grades severity on the day using Appendix B.",
        "First aid or medical response is given, the area is made safe and evidence preserved.",
        "Family and GP are informed where needed.",
      ]},
      { title: "5. Investigation", lines: [
        "Witnesses are interviewed, records reviewed and the scene inspected.",
        "The root cause is identified and corrective action agreed.",
        "Serious incidents receive a root cause review within 10 working days.",
      ]},
      { title: "6. Support and duty of candour", lines: [
        "Residents and staff receive emotional support and any medical treatment; care plans are adjusted and risks reassessed.",
        "Under Regulation 20A we act openly, tell the person and family what happened and offer a sincere apology and explanation.",
      ]},
      { title: "7. Learning and prevention of recurrence", lines: [
        "All incidents are reviewed for learning; trends are analysed monthly.",
        "Corrective action may change policies, procedures, training or equipment.",
      ]},
      { title: "8. Communication, confidentiality and timeliness", lines: [
        "Findings and actions are communicated to relevant staff and families are kept informed of progress.",
        "Information is treated confidentially and shared only with those who need it.",
      ]},
      { title: "9. Training, monitoring and review", lines: [
        "All staff are trained in reporting, investigating and responding to incidents.",
        "The policy is reviewed annually and after any significant incident, in line with CQC and Wakefield Council requirements.",
      ]},
      { title: "Appendix A - Notification timescales", lines: [
        "Internal report by end of shift. Family same day for serious matters. Police immediately for suspected crime.",
        "CQC notified without delay for notifiable events with written follow up as required.",
      ]},
      { title: "Appendix B - Severity grading", lines: [
        "Low: no harm or first aid only, manager review. Medium: injury requiring treatment, manager investigation.",
        "High: serious injury, hospital admission or death, root cause review and CQC notification.",
      ]},
    ],
  },
  {
    id: "upd-complaints",
    code: "GOV-010",
    title: "Complaints",
    category: "governance",
    cqcFocus: "Responsive",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "A fair, transparent and accessible five stage complaints procedure that treats every complaint as an opportunity to learn, with advocacy support and clear escalation to the CQC, Wakefield Council and the Ombudsman.",
    keyPoints: [
      "Complaints are acknowledged within 3 working days and resolved within 21 working days.",
      "Investigations are completed within 10 working days unless the case is complex.",
      "Complainants can access advocacy at any stage.",
      "Every upheld complaint leads to corrective action and shared learning.",
    ],
    relatedRegulations: [
      "Regulation 16 Receiving and Acting on Complaints",
      "Local Government and Social Care Ombudsman powers",
      "Equality Act 2010 reasonable adjustments",
    ],
    sections: [
      { title: "1. Introduction and purpose", lines: [
        "Complaints are valuable feedback. They are handled promptly, fairly and effectively so we can learn and improve.",
      ]},
      { title: "2. Scope and types of complaints", lines: [
        "Covers quality of care, staff behaviour, premises, communication and any other aspect of the service.",
        "Informal complaints are resolved locally where possible; formal complaints are acknowledged in writing and investigated by a senior member of staff.",
      ]},
      { title: "3. Receiving complaints", lines: [
        "Complaints can be made in person, by letter, by email to hr@roopcottage.co.uk, by form, or to any staff member, the manager, the chief executive or the nominated individual.",
        "Reasonable adjustments and accessible formats are offered so everyone can complain.",
      ]},
      { title: "4. Stages of the procedure", lines: [
        "Stage 1: the complaint is made with as much detail as possible, as soon as possible after the event.",
        "Stage 2: an investigator not involved in the matter gathers information and completes the investigation within 10 working days, keeping the complainant updated.",
        "Stage 3: the manager and compliance team decide; the complainant receives a written response including any corrective action, apology or redress.",
        "Stage 4: an appeal may be made within 28 days to an independent panel of three people not involved in the original complaint.",
        "Stage 5: if still not satisfied, the complainant may contact Wakefield Council and the CQC within 12 months.",
      ]},
      { title: "5. Resolution, apology and redress", lines: [
        "Where a complaint is upheld we take corrective action, offer a sincere apology and consider redress such as reimbursement, additional support or care plan changes.",
      ]},
      { title: "6. Advocacy", lines: [
        "Every complainant has the right to advocacy; the home provides information, helps connect residents with advocates and facilitates communication.",
      ]},
      { title: "7. Escalation to regulators", lines: [
        "The CQC can be contacted by its online form, by phone on 03000 616161 or at Citygate, Gallowgate, Newcastle upon Tyne, NE1 4PA.",
        "The Local Government and Social Care Ombudsman may intervene after our procedure, on 0300 061 0614.",
        "Wakefield Council Health Housing and Adult Services: 0345 8 503 503, social-care-complaints@wakefield.gov.uk.",
      ]},
      { title: "8. Confidentiality, timeliness, training and review", lines: [
        "Complaints are treated confidentially and shared only with those who need them.",
        "All staff receive complaints handling training; the policy is reviewed annually and after any significant complaint.",
      ]},
      { title: "Appendix A - Timelines", lines: [
        "Acknowledgement within 3 working days. Investigation within 10 working days. Full response within 21 working days.",
        "Appeal within 28 days of the decision. Escalation to council or CQC within 12 months.",
      ]},
      { title: "Appendix B - Contacts", lines: [
        "Home: hr@roopcottage.co.uk, the manager, the chief executive and the nominated individual.",
        "CQC 03000 616161. Ombudsman 0300 061 0614. Wakefield Council 0345 8 503 503.",
      ]},
    ],
  },
  {
    id: "sop-admission",
    code: "SOP-001",
    title: "SOP Admission and Settling In",
    category: "core",
    cqcFocus: "Responsive",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "A safe, welcoming and person centred admission with every assessment completed in the first 72 hours and care plans started on day one.",
    keyPoints: [
      "The comprehensive admission form is completed on day one.",
      "GP registration and medication reconciliation within 24 hours.",
      "All risk assessments completed within 72 hours.",
      "A key worker is assigned and meets the resident within 7 days.",
    ],
    relatedRegulations: ["Regulation 9 Person Centred Care", "Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete the pre-admission assessment and risk screening before arrival.",
        "2. Prepare the room and welcome the resident and family on arrival.",
        "3. Complete the comprehensive admission form in the Admissions module on day one.",
        "4. Confirm GP registration and complete medication reconciliation within 24 hours.",
        "5. Complete MUST, falls, pressure, moving handling and capacity assessments within 72 hours.",
        "6. Start care plans within 24 hours and review them within 7 days.",
        "7. Assign a key worker who meets the resident within 7 days.",
        "8. Record family contact details and how they wish to be involved.",
      ]},
      { title: "Records and monitoring", lines: [
        "Admission record, assessments, care plans and property inventory are filed in the resident record.",
        "The manager reviews every admission within the first week.",
      ]},
    ],
  },
  {
    id: "sop-med-admin",
    code: "SOP-002",
    title: "SOP Medication Administration and MAR Recording",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Safe, accurate and dignified administration of medication by trained and assessed staff, with immediate MAR signing and controlled drug controls.",
    keyPoints: [
      "Only trained and competency assessed staff administer medication.",
      "The five rights are checked: resident, drug, dose, route and time.",
      "Every dose is signed at the time of administration, never before.",
      "Controlled drugs need two staff, a balance check and witnessed administration.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment", "Misuse of Drugs Regulations 2001"],
    sections: [
      { title: "Procedure", lines: [
        "1. Wash hands and use PPE before the round.",
        "2. Check the MAR and confirm the five rights for each item.",
        "3. Administer and sign immediately; never pre-sign.",
        "4. Record refusals and missed doses and action them per SOP-003.",
        "5. Give PRN only per protocol and record the effect.",
        "6. Record creams and eye drops as administered.",
        "7. For controlled drugs: two staff, balance check, witness and sign.",
        "8. Return medication to secure storage and lock after the round.",
        "9. Do not allow interruptions during the round except emergencies.",
      ]},
      { title: "Records and monitoring", lines: [
        "MAR charts, CD book and PRN protocols are the records.",
        "A medication check runs at every shift change and a MAR audit runs monthly.",
      ]},
    ],
  },
  {
    id: "sop-med-error",
    code: "SOP-003",
    title: "SOP Medication Errors, Missed Doses and Escalation",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Resident safety first: every error or missed dose is assessed, reported, recorded and learned from, with CQC notified where harm occurs.",
    keyPoints: [
      "The resident's wellbeing is checked first and clinical advice obtained.",
      "The error is recorded on the MAR and an incident form the same shift.",
      "The pharmacy corrects supply and dosing where needed.",
      "Errors are trended monthly and retraining follows patterns.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment", "Regulation 18 Notification of Incidents"],
    sections: [
      { title: "Procedure", lines: [
        "1. Check the resident and obtain clinical advice from GP, pharmacy or 111 as needed.",
        "2. Record the error on the MAR and complete an incident form the same shift.",
        "3. Inform the manager and the family where appropriate.",
        "4. Arrange corrective supply or dosing with the pharmacy.",
        "5. Notify CQC where the error caused or could cause serious harm.",
        "6. Review trends monthly and retrain where patterns appear.",
      ]},
      { title: "Records and monitoring", lines: [
        "MAR annotations, incident forms and the error trend report.",
        "The manager reviews every error and signs the monthly trend.",
      ]},
    ],
  },
  {
    id: "sop-falls",
    code: "SOP-004",
    title: "SOP Falls Response and Post Falls Review",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "A calm, safe and clinical response to every fall, with injuries identified early, families informed and a post falls review that prevents the next fall.",
    keyPoints: [
      "The resident is never moved until assessed.",
      "Clinical advice is obtained the same day and 999 called for serious injury.",
      "A post falls review is completed within 72 hours.",
      "The falls risk assessment and care plan are updated after every fall.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Do not move the resident; make the area safe and call for help.",
        "2. Assess response, airway, breathing and circulation and give first aid.",
        "3. Check for pain, deformity, bleeding and head injury.",
        "4. Use the return to standing procedure only when safe to do so.",
        "5. Inform the manager and obtain clinical advice from GP, 111 or 999.",
        "6. Complete the incident form and body map the same shift.",
        "7. Inform the family the same day.",
        "8. Hold a post falls review within 72 hours covering medication, environment, footwear and vision.",
        "9. Update the falls risk score and care plan and add actions to the action plan.",
      ]},
      { title: "Records and monitoring", lines: [
        "Incident form, body map, post falls review and updated care plan.",
        "Falls are trended monthly and reviewed at the quality meeting.",
      ]},
    ],
  },
  {
    id: "sop-pressure",
    code: "SOP-005",
    title: "SOP Pressure Care and Skin Integrity",
    category: "clinical",
    cqcFocus: "Effective",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Prevents pressure damage using the SSKIN bundle: skin checks, surfaces, keep moving, incontinence care and nutrition, with early referral to tissue viability.",
    keyPoints: [
      "Skin is assessed on admission and daily for at risk residents.",
      "Repositioning happens exactly as charted and is recorded every time.",
      "Non blanching redness is escalated the same day with a tissue viability referral within 24 hours.",
      "Nutrition is protected with MUST scoring and dietitian referral.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Assess skin on admission and daily for at risk residents using SSKIN.",
        "2. Reposition exactly as charted and record every turn.",
        "3. Provide the prescribed pressure relieving surface and check it works daily.",
        "4. Complete MUST scoring and refer to the dietitian where the score is 2 or more.",
        "5. Report non blanching redness to the manager the same day and refer to tissue viability within 24 hours.",
        "6. Photograph wounds with consent and measure weekly.",
        "7. Review the care plan after any change in condition.",
      ]},
      { title: "Records and monitoring", lines: [
        "Repositioning charts, wound charts, MUST scores and referrals.",
        "Pressure damage prevalence is reviewed monthly.",
      ]},
    ],
  },
  {
    id: "sop-outbreak",
    code: "SOP-006",
    title: "SOP Infection Outbreak Management",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Recognises outbreaks early, isolates quickly, notifies the right bodies and stands down safely, protecting residents, staff and visitors.",
    keyPoints: [
      "Two or more linked cases are treated as an outbreak and escalated the same day.",
      "UKHSA health protection team advice is sought and followed.",
      "CQC is notified where required.",
      "Stand down follows 48 hours symptom free or UKHSA advice.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment", "Regulation 18 Notification of Incidents"],
    sections: [
      { title: "Procedure", lines: [
        "1. Recognise a cluster of two or more cases and inform the manager immediately.",
        "2. Isolate affected residents and apply standard precautions with correct PPE.",
        "3. Start enhanced cleaning; use chlorine based products for norovirus.",
        "4. Notify the UKHSA health protection team and follow their advice; notify CQC where required.",
        "5. Restrict admissions, transfers and communal dining per advice.",
        "6. Exclude symptomatic staff until 48 hours symptom free.",
        "7. Keep families informed throughout.",
        "8. Deep clean on stand down and close the outbreak log with a learning review.",
      ]},
      { title: "Records and monitoring", lines: [
        "Outbreak log, line list, cleaning records and notifications.",
        "The manager reviews the log daily until stand down.",
      ]},
    ],
  },
  {
    id: "sop-fire",
    code: "SOP-007",
    title: "SOP Fire Evacuation",
    category: "environment",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "A calm, practiced evacuation using PEEPs for every resident, horizontal evacuation first, and a controlled handover to the fire service.",
    keyPoints: [
      "Every resident has a personal emergency evacuation plan reviewed every six months.",
      "Staff evacuate horizontally away from the fire before any vertical movement.",
      "The grab bag with the resident register and medication list goes with the evacuation.",
      "Nobody re-enters until the fire service declares the building safe.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment", "Regulatory Reform (Fire Safety) Order 2005"],
    sections: [
      { title: "Procedure", lines: [
        "1. Raise the alarm on discovery and call 999.",
        "2. Evacuate the affected zone using each resident's PEEP, moving horizontally first.",
        "3. Close doors behind you to contain smoke and fire.",
        "4. Take the grab bag with the resident register and medication list.",
        "5. Complete the headcount at the assembly point.",
        "6. Meet the fire service and hand over the register and fire plan.",
        "7. Do not re-enter until the fire service declares the building safe.",
        "8. Put welfare arrangements in place and hold a debrief.",
        "9. Record the event in the fire log and review the response.",
      ]},
      { title: "Records and monitoring", lines: [
        "Fire log, PEEPs, drill records and debrief notes.",
        "Drills run twice daily pattern per quarter with one night drill.",
      ]},
    ],
  },
  {
    id: "sop-handling",
    code: "SOP-008",
    title: "SOP Moving and Handling",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Protects residents and staff from injury: individual handling plans, tested equipment, pre-use checks and no manual lifting except in emergencies.",
    keyPoints: [
      "Every resident has an individual handling plan that staff follow exactly.",
      "Hoists and slings are LOLER tested and checked before every use.",
      "Two staff carry out hoisting unless the plan says otherwise.",
      "Defective equipment is quarantined and reported immediately.",
    ],
    relatedRegulations: ["LOLER 1998", "PUWER 1998", "Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete an individual handling assessment for every resident.",
        "2. Document the handling plan and communicate it at handover.",
        "3. Only trained and assessed staff carry out handling tasks.",
        "4. Complete pre-use checks of hoists, slings and beds.",
        "5. Use the prescribed sling type and size for each resident.",
        "6. Use two staff for hoisting unless the plan states otherwise.",
        "7. Never drag or manually lift except in a life threatening emergency.",
        "8. Review the plan after any fall, change in condition or annually.",
        "9. Quarantine and report defective equipment immediately.",
      ]},
      { title: "Records and monitoring", lines: [
        "Handling plans, pre-use check sheets, LOLER certificates and defect reports.",
        "The manager audits checks monthly.",
      ]},
    ],
  },
  {
    id: "sop-restraint",
    code: "SOP-009",
    title: "SOP Restraint and Restriction Recording",
    category: "safeguarding",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Restraint is a last resort, least restrictive and in best interests; every episode is recorded the same shift, reviewed within 24 hours and trended for MCA and DoLS oversight.",
    keyPoints: [
      "De-escalation is always tried before any restraint.",
      "Every episode is recorded the same shift with reason, type, duration and outcome.",
      "A review with alternatives happens within 24 hours.",
      "Ongoing restriction triggers an MCA and DoLS review.",
    ],
    relatedRegulations: ["Mental Capacity Act 2005 section 6", "Regulation 13 Safeguarding", "Regulation 20A Duty of Candour"],
    sections: [
      { title: "Procedure", lines: [
        "1. Try de-escalation and less restrictive options first.",
        "2. Use restraint only to prevent harm, proportionately and for the shortest time.",
        "3. Record the episode the same shift with reason, type, duration and outcome.",
        "4. Inform the manager and, where appropriate, the family the same day.",
        "5. Check the resident for physical harm and wellbeing afterwards.",
        "6. Hold a review within 24 hours including alternatives.",
        "7. Add the episode to the restraint register.",
        "8. Where restriction is ongoing, review the MCA and DoLS authorisation.",
      ]},
      { title: "Records and monitoring", lines: [
        "Restraint register, episode records and reviews.",
        "Trends are reviewed monthly by the manager.",
      ]},
    ],
  },
  {
    id: "sop-handover",
    code: "SOP-010",
    title: "SOP Shift Handover",
    category: "core",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "A structured handover that passes on every risk, change and outstanding task so no resident need is missed between shifts.",
    keyPoints: [
      "Handover covers new admissions, changes in condition, risks and outstanding tasks.",
      "Medication gaps, refusals and skin changes are always handed over.",
      "Outstanding tasks are assigned to named staff.",
      "Both shift leads sign the handover record.",
    ],
    relatedRegulations: ["Regulation 12 Safe Care and Treatment", "Regulation 17 Good Governance"],
    sections: [
      { title: "Procedure", lines: [
        "1. The outgoing lead prepares the shift summary before handover time.",
        "2. Walk through each resident with the incoming lead, highest risk first.",
        "3. Hand over medication items: MAR gaps, refusals, PRN given and new prescriptions.",
        "4. Hand over skin integrity, falls, behaviour and mood changes.",
        "5. Assign every outstanding task to a named member of staff.",
        "6. Complete and sign the handover record; both leads sign.",
        "7. Share the key points with the whole team at the shift huddle.",
      ]},
      { title: "Records and monitoring", lines: [
        "The signed handover record is kept with the daily notes.",
        "The manager spot checks handovers weekly.",
      ]},
    ],
  },
  {
    id: "sop-water",
    code: "SOP-011",
    title: "SOP Water Safety and Temperature Monitoring",
    category: "environment",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Controls legionella and scalding risk through temperature control, flushing, cleaning and same day escalation of out of range results.",
    keyPoints: [
      "Calorifier stored at 60 C or above and checked monthly.",
      "Sentinel taps checked weekly: cold below 20 C, hot at 50 C or 45 C at TMV outlets.",
      "Infrequently used outlets are flushed weekly.",
      "Out of range results are reported the same day and retested.",
    ],
    relatedRegulations: ["HSG274 Legionella control", "Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Check the calorifier temperature monthly at 60 C or above.",
        "2. Take weekly sentinel tap temperatures: cold below 20 C after two minutes, hot at 50 C or above within one minute, or 45 C at TMV outlets.",
        "3. Flush infrequently used outlets weekly and record it.",
        "4. Clean and disinfect shower heads monthly.",
        "5. Report any out of range result to the manager the same day, retest and escalate per the water safety plan.",
        "6. Keep all records for two years and review them at the water safety group.",
      ]},
      { title: "Records and monitoring", lines: [
        "Temperature log, flushing log, cleaning log and escalation notes.",
        "The manager reviews the logs weekly.",
      ]},
    ],
  },
  {
    id: "sop-food",
    code: "SOP-012",
    title: "SOP Food Hygiene and Mealtime Service",
    category: "core",
    cqcFocus: "Effective",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Safe food and dignified mealtimes: temperature control, allergy safe special diets, choice at every meal and protected mealtime assistance.",
    keyPoints: [
      "Fridge temperatures 0 to 5 C are recorded twice daily.",
      "Special diets and textures are checked against the allergy matrix at every service.",
      "Residents always have a real choice at every meal.",
      "Mealtimes are protected with help available for everyone who needs it.",
    ],
    relatedRegulations: ["Food Safety Act 1990", "Regulation 12 Safe Care and Treatment"],
    sections: [
      { title: "Procedure", lines: [
        "1. Record fridge and freezer temperatures twice daily; fridge 0 to 5 C.",
        "2. Serve from the four week menu with a choice at every meal.",
        "3. Check special diets and texture modifications against the allergy matrix before each service.",
        "4. Protect mealtimes; identify residents who need help and provide it.",
        "5. Keep the probe thermometer calibrated and record checks.",
        "6. Complete the kitchen cleaning schedule and HACCP records.",
        "7. Record and dispose of waste food safely.",
      ]},
      { title: "Records and monitoring", lines: [
        "Temperature logs, HACCP records, cleaning schedule and menu choice sheets.",
        "The manager audits the kitchen monthly.",
      ]},
    ],
  },
];
